import { EResourceSortAttribute } from '@/api/resources/types/enum/resource-sort-attribute';
import { ESortOrder } from '@/api/resources/types/enum/sort-order';
import { ISearchItem, IAutocompleteSuggestion } from '@/content-library/search/types/search';
import { ESearchItemType, EAccessibility } from '@/content-library/search/types/enums/search-types';
import { ILoadResourcesFilter } from '@/content-library/types/load-resources-filter';
import { ContentLibraryHelper } from '@/content-library/helpers/content-library.helper';
import { IResource } from '@/api/resources/types/resource';
import { translate } from '@/helpers/translation.helper';

export class SearchHelper {
  static getResourceEditURL(resource: IResource): string {
    const resourceTypePath: string = ContentLibraryHelper.getParamFromResourceType(
      resource.resourceType
    );
    if (resourceTypePath && resource?.id) {
      return `content-library/edit/${resourceTypePath}/${resource.id}`;
    }
  }

  static getSearchItemTooltipText(searchItem: ISearchItem): string {
    if (!this.isAutocompleteSuggestion(searchItem)) {
      return '';
    }

    const autocompleteSuggestion = searchItem.data as IAutocompleteSuggestion;

    switch (autocompleteSuggestion.accessibility) {
      case EAccessibility.AccessDenied:
        return translate('you-do-not-have-access');
      case EAccessibility.NotFound:
        return translate('document-has-been-removed');
      case EAccessibility.OtherError:
        return translate('you-cannot-edit-this-resource');
      default:
        return '';
    }
  }

  static getNoSearchItemsMessage(isAutocompleting: boolean): string {
    return isAutocompleting ? translate('no-matching-resources') : translate('no-recent-searches');
  }

  static getInputPlaceholderText(isExpanded: boolean): string {
    return isExpanded ? '' : translate('search-content');
  }

  static getSearchItemListTitle(isAutocompleting: boolean): string {
    return isAutocompleting ? '' : translate('recent-searches');
  }

  static isAutocompleteSuggestion(searchItem: ISearchItem): boolean {
    return searchItem.type === ESearchItemType.AutocompleteSuggestion;
  }

  static isRecentlySearched(searchItem: ISearchItem): boolean {
    return searchItem.type === ESearchItemType.RecentlySearched;
  }

  static isSearchItemAccessible(searchItem: ISearchItem): boolean {
    if (!this.isAutocompleteSuggestion(searchItem)) {
      return true;
    }
    const autocompleteSuggestion = searchItem.data as IAutocompleteSuggestion;
    return autocompleteSuggestion.accessibility === EAccessibility.Normal;
  }

  static createSearchOptions(searchTerm: string, directory: string): Partial<ILoadResourcesFilter> {
    return {
      sortBy: EResourceSortAttribute.UpdatedAt,
      sortOrder: ESortOrder.Desc,
      searchTerms: [searchTerm],
      directory,
    };
  }

  static createRecentSearchItems(recentSearches: string[]): ISearchItem[] {
    return recentSearches.map((recentSearch: string) => ({
      label: recentSearch,
      icon: 'search',
      type: ESearchItemType.RecentlySearched,
      data: recentSearch,
    }));
  }

  static createAutocompleteSearchItems(
    autocompleteSuggestions: IAutocompleteSuggestion[]
  ): ISearchItem[] {
    return autocompleteSuggestions.map((autocompleteSuggestion: IAutocompleteSuggestion) => ({
      label: autocompleteSuggestion.resource.title,
      icon: ContentLibraryHelper.getIcon(autocompleteSuggestion.resource),
      type: ESearchItemType.AutocompleteSuggestion,
      data: autocompleteSuggestion,
    }));
  }

  static addRecentSearchTerm(
    existingRecentSearches: string[],
    newSearchTerm: string,
    maxLength: number
  ): string[] {
    return [newSearchTerm]
      .concat(
        existingRecentSearches.filter(term => term.toLowerCase() !== newSearchTerm.toLowerCase())
      )
      .slice(0, maxLength);
  }
}
